import { type ColumnDef } from '@tanstack/react-table';
import { Server, Heart } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { DataTable } from '@/components/shared/DataTable';
import { StatusBadge } from '@/components/shared/StatusBadge';
import { LoadingSpinner } from '@/components/shared/LoadingSpinner';
import { EmptyState } from '@/components/shared/EmptyState';
import { JsonViewer } from '@/components/shared/JsonViewer';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  useGetServicesQuery,
  useGetServiceQuery,
} from '@/store/api/apiSlice';
import { useState } from 'react';
import type { ServiceSubject, EndpointDescriptor } from '@/types';
import { format, formatDistanceToNow } from 'date-fns';

/**
 * Returns a valid Date or null. Guards against undefined/invalid timestamps
 * (e.g. services registered in-memory without dates) that would make date-fns
 * throw "Invalid time value".
 */
function safeDate(value?: string | Date | null): Date | null {
  if (!value) return null;
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
}

export function ServicesPage() {
  const [selectedService, setSelectedService] = useState<ServiceSubject | null>(null);

  // Poll so a transient/cached lifecycle status is never displayed: the
  // subjects registry self-heals within one heartbeat interval, and the page
  // must reflect that without a manual reload.
  const { data: services, isLoading } = useGetServicesQuery(undefined, {
    pollingInterval: 15000,
    refetchOnMountOrArgChange: true,
  });
  // Subjects subjects have no `_id`; the details endpoint is keyed by
  // serviceName (GET /subjects/:serviceName). Using serviceName avoids firing
  // /services/undefined and getting a permanent 404.
  const { data: serviceDetail, isLoading: detailLoading } = useGetServiceQuery(
    selectedService?.serviceName ?? '',
    { skip: !selectedService },
  );

  const serviceList = (services as ServiceSubject[] | undefined) ?? [];

  const columns: ColumnDef<ServiceSubject, unknown>[] = [
    {
      accessorKey: 'serviceName',
      header: 'Service',
      cell: ({ row }) => (
        <div className="flex items-center gap-2">
          <Server className="h-4 w-4 text-muted-foreground" />
          <div>
            <p className="font-medium">{row.original.serviceName}</p>
            <p className="text-xs text-muted-foreground">{row.original.queueName}</p>
          </div>
        </div>
      ),
    },
    {
      accessorKey: 'status',
      header: 'Status',
      cell: ({ row }) => <StatusBadge status={row.original.status} />,
    },
    {
      accessorKey: 'endpoints',
      header: 'Endpoints',
      cell: ({ row }) => `${row.original.endpoints.length} endpoint(s)`,
    },
    {
      accessorKey: 'lastHeartbeat',
      header: 'Last Heartbeat',
      cell: ({ row }) => {
        const d = safeDate(row.original.lastHeartbeat);
        return d ? formatDistanceToNow(d, { addSuffix: true }) : '—';
      },
    },
    {
      accessorKey: 'registeredAt',
      header: 'Registered',
      cell: ({ row }) => {
        const d = safeDate(row.original.registeredAt);
        return d ? format(d, 'MMM d, yyyy') : '—';
      },
    },
    {
      id: 'actions',
      header: '',
      cell: ({ row }) => (
        <Button variant="ghost" size="sm" onClick={() => setSelectedService(row.original)}>
          Details
        </Button>
      ),
    },
  ];

  const detail = (serviceDetail as ServiceSubject | undefined) ?? selectedService;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold">Services</h1>
        <p className="text-sm text-muted-foreground">
          Registered microservices and their health status
        </p>
      </div>

      <Card>
        <CardContent className="pt-6">
          {isLoading ? (
            <LoadingSpinner className="py-12" />
          ) : serviceList.length === 0 ? (
            <EmptyState
              title="No services registered"
              description="Services will appear here once they register with the gateway"
              icon={<Server className="h-8 w-8" />}
            />
          ) : (
            <DataTable columns={columns} data={serviceList} />
          )}
        </CardContent>
      </Card>

      {/* Service detail dialog */}
      <Dialog open={!!selectedService} onOpenChange={(open) => !open && setSelectedService(null)}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>Service Details</DialogTitle>
            <DialogDescription>
              {detail?.serviceName} — {detail?.queueName}
            </DialogDescription>
          </DialogHeader>
          {detailLoading ? (
            <LoadingSpinner className="py-8" />
          ) : detail ? (
            <div className="space-y-6">
              {/* Overview */}
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-muted-foreground">Status:</span>{' '}
                  <StatusBadge status={detail.status} />
                </div>
                <div>
                  <span className="text-muted-foreground">Queue:</span> {detail.queueName}
                </div>
                <div>
                  <span className="text-muted-foreground">Registered:</span>{' '}
                  {safeDate(detail.registeredAt)
                    ? format(safeDate(detail.registeredAt)!, 'MMM d, yyyy HH:mm')
                    : '—'}
                </div>
                <div>
                  <span className="text-muted-foreground">Last Heartbeat:</span>{' '}
                  {safeDate(detail.lastHeartbeat)
                    ? formatDistanceToNow(safeDate(detail.lastHeartbeat)!, { addSuffix: true })
                    : 'Never'}
                </div>
              </div>

              {/* Endpoints */}
              <div>
                <h3 className="text-sm font-semibold mb-2">
                  Endpoints ({detail.endpoints.length})
                </h3>
                <div className="rounded-md border">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b bg-muted/50">
                        <th className="px-3 py-2 text-left font-medium">Method</th>
                        <th className="px-3 py-2 text-left font-medium">Path</th>
                        <th className="px-3 py-2 text-left font-medium">Description</th>
                        <th className="px-3 py-2 text-left font-medium">Public</th>
                      </tr>
                    </thead>
                    <tbody>
                      {detail.endpoints.map((ep, i) => (
                        <tr key={i} className="border-b last:border-0">
                          <td className="px-3 py-2">
                            <span className="rounded bg-muted px-1.5 py-0.5 text-xs font-mono font-semibold">
                              {ep.method}
                            </span>
                          </td>
                          <td className="px-3 py-2 font-mono text-xs">{ep.path}</td>
                          <td className="px-3 py-2 text-muted-foreground">{ep.description ?? '—'}</td>
                          <td className="px-3 py-2">
                            {ep.isPublic ? (
                              <span className="text-green-600 text-xs">Yes</span>
                            ) : (
                              <span className="text-xs">No</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Metadata */}
              {detail.metadata && Object.keys(detail.metadata).length > 0 && (
                <div>
                  <h3 className="text-sm font-semibold mb-2">Metadata</h3>
                  <JsonViewer data={detail.metadata} />
                </div>
              )}
            </div>
          ) : null}
        </DialogContent>
      </Dialog>
    </div>
  );
}
